import { get } from 'lodash';
import { useMemo } from 'react';
import { Helmet } from 'react-helmet';
import { Navigate, useLocation, useNavigate } from 'hotel/navigation';

import { ButtonLink } from '../../components/Button';
import Loading from '../../components/Loading';
import RequestChangesForm, {
  RequestChangesFormValues,
} from '../../components/Requests/RequestChangesForm';
import RosterBulkEditDetails from '../../components/Roster/RosterBulkEditDetails';
import { SectionHeader } from '../../components/SectionHeader';
import {
  CreateRosterRequestInput,
  RosterRequestType,
  useCreateRosterRequestMutation,
  useRosterItemsForRequestQuery,
} from '../../generated/graphql';
import { useTravelerSelfCheck } from '../../hooks/useTravelerSelfCheck';
import { popToast } from '../../utilities/popToast';

export default function RequestChanges() {
  const navigate = useNavigate();
  const { isOnlyTraveler, isOwnRosterEntry } = useTravelerSelfCheck();
  const [createRosterRequest, { loading }] = useCreateRosterRequestMutation({
    onCompleted: () => {
      popToast(`Request successfully submitted!`);
    },
    onError: (error) => {
      popToast(`Error submitting request: ${error.message}`, {
        type: 'error',
      });
    },
  });

  const location = useLocation();

  const rosterRoomAssignmentIds: string[] = get(
    location,
    'state.rosterRoomAssignmentIds',
    [],
  );

  const requestType: RosterRequestType = get(
    location,
    'state.requestType',
    null,
  );

  const onSubmit = (values: RequestChangesFormValues) => {
    if (isOnlyTraveler && rosterItemsData?.rosterItems) {
      const hasInvalidEntries = rosterItemsData.rosterItems.some(
        (item) => !isOwnRosterEntry(item.guest?.id),
      );
      if (hasInvalidEntries) {
        popToast('You can only request changes for your own reservations', {
          type: 'error',
        });
        return;
      }
    }

    const { checkInDate, checkOutDate, details, requestType, controlNumber } =
      values;

    const input: CreateRosterRequestInput = {
      details,
      requestType,
      controlNumber,
    };

    if (checkInDate && checkOutDate) {
      input.requestDataDateChange = {
        checkInDate,
        checkOutDate,
      };
    }

    if (rosterRoomAssignmentIds.length > 0) {
      input.rosterRoomAssignments = rosterRoomAssignmentIds;
    }

    createRosterRequest({
      variables: {
        input,
      },
    });
  };

  const { data: rosterItemsData, loading: loadingRosterItems } =
    useRosterItemsForRequestQuery({
      variables: {
        ids: rosterRoomAssignmentIds,
      },
      skip: !rosterRoomAssignmentIds || rosterRoomAssignmentIds.length < 1,
    });

  // Check if traveler has invalid entries (entries that don't belong to them)
  const hasInvalidTravelerEntries = useMemo(() => {
    if (!isOnlyTraveler || !rosterItemsData?.rosterItems) {
      return false;
    }
    return rosterItemsData.rosterItems.some(
      (item) => !isOwnRosterEntry(item.guest?.id),
    );
  }, [isOnlyTraveler, rosterItemsData, isOwnRosterEntry]);

  // Show loading state while fetching roster items for travelers
  if (
    isOnlyTraveler &&
    loadingRosterItems &&
    rosterRoomAssignmentIds.length > 0
  ) {
    return <Loading />;
  }

  // Redirect travelers who try to access other users' roster entries
  if (hasInvalidTravelerEntries) {
    popToast('You can only request changes for your own reservations', {
      type: 'error',
    });
    return <Navigate to="/roster" replace />;
  }

  return (
    <div className="flex items-start justify-center p-5 sm:p-10">
      <Helmet>
        <title>Request Changes</title>
      </Helmet>

      <div className="flex-1">
        <>
          <SectionHeader headline="Request Changes">
            <ButtonLink to="/roster" text="Return to Live Roster" />
          </SectionHeader>
          <div className="flex flex-col pb-5">
            {rosterItemsData && (
              <RosterBulkEditDetails
                rosterRoomAssignments={rosterItemsData.rosterItems}
                removeAssignment={(removedAssignmentId) => {
                  navigate(
                    '.',

                    {
                      state: {
                        rosterRoomAssignmentIds:
                          rosterRoomAssignmentIds?.filter(
                            (id) => id !== removedAssignmentId,
                          ),
                      },
                    },
                  );
                }}
              />
            )}
          </div>
          {loading ? (
            <Loading />
          ) : (
            <RequestChangesForm
              onSubmit={onSubmit}
              initialValues={{
                requestType: requestType,
              }}
            />
          )}
        </>
      </div>
    </div>
  );
}
